import {config} from './config';
import type {ConfirmPaymentParams} from './payment-modal';
import type {
  CreateApplePaySessionRequest,
  CreatePaymentRequest,
  CreateTokenRequest,
  CreateTokenResponse,
  GetPaymentMethodsRequest,
  GetPaymentMethodsResponse,
  PaymentResult,
  SendPaymentReceiptRequest,
  ValidateBizumPhoneRequest
} from './types';
import {getClientEnv, getClientFingerprint, stringify, withError} from './utils';

type RequestParams = {
  method?: string;
  headers?: any;
  body?: any;
};
const request = async (url: string, {method = 'GET', headers, body}: RequestParams = {}) => {
  if (method === 'POST') headers = {'Content-Type': 'application/json', ...headers};
  const res = await fetch(`${config.apiUrl}${url}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined
  });
  const [, result] = await withError(res.json());
  if (!res.ok) throw result || new Error(res.statusText);
  return result;
};

const pmCache: Record<string, Promise<GetPaymentMethodsResponse>> = {};

export const getPaymentMethods = async ({
  accountId,
  paymentId
}: GetPaymentMethodsRequest): Promise<GetPaymentMethodsResponse> => {
  let params: GetPaymentMethodsRequest;
  if (paymentId) params = {paymentId};
  else if (accountId) params = {accountId};
  else throw new Error('You need to provide paymentId or accountId');
  const query = stringify(params);
  if (query in pmCache) return pmCache[query];
  const promise = request(`/client-payment-methods?${query}`);
  pmCache[query] = promise;
  promise.catch(() => delete pmCache[query]);
  return promise;
};

export const createToken = async ({
  paymentMethod,
  paymentId,
  sessionId,
  amount,
  currency,
  accountId,
  transactionType
}: CreateTokenRequest): Promise<CreateTokenResponse> => {
  let body: CreateTokenRequest;
  if (paymentId) {
    body = {paymentId, paymentMethod};
  } else if (accountId) {
    body = {accountId, paymentMethod, sessionId, amount, currency, transactionType};
  } else throw new Error('You need to provide paymentId or accountId');
  const res: CreateTokenResponse = await request('/tokens', {
    method: 'POST',
    headers: {'X-Client-Env': getClientEnv(), 'X-Client-Fingerprint': await getClientFingerprint()},
    body: body
  });
  // Normalize: backend returns paymentToken, SDK uses token
  if (res.paymentToken && !res.token) res.token = res.paymentToken;
  return res;
};

export const getPayment = async (paymentId: string): Promise<PaymentResult> => {
  return request(`/payments/${paymentId}/client-get`);
};

export const confirmPayment = async ({
  paymentId,
  ...body
}: ConfirmPaymentParams): Promise<PaymentResult> => {
  return request(`/payments/${paymentId}/client-confirm`, {
    method: 'POST',
    headers: {'X-Client-Env': getClientEnv(), 'X-Client-Fingerprint': await getClientFingerprint()},
    body
  });
};

export const createPayment = async ({
  signature,
  ...body
}: CreatePaymentRequest): Promise<PaymentResult> => {
  return request('/client-payments', {
    method: 'POST',
    headers: {
      'X-Client-Env': getClientEnv(),
      'X-Client-Fingerprint': await getClientFingerprint(),
      'Client-Signature': signature
    },
    body
  });
};

export const sendPaymentReceipt = async ({
  paymentId,
  customerEmail,
  signature,
  language
}: SendPaymentReceiptRequest): Promise<PaymentResult> => {
  return request(`/payments/${paymentId}/client-receipt`, {
    method: 'POST',
    body: {customerEmail, signature, language}
  });
};

export const createApplePaySession = async (body: CreateApplePaySessionRequest) => {
  return request(`/apple-pay/sessions`, {method: 'POST', body});
};

export const validateBizumPhone = async (
  req: ValidateBizumPhoneRequest
): Promise<{isValid: boolean}> => {
  return request(`/bizum/validate-phone`, {method: 'POST', body: req});
};
